import {
  Slider,
  Grid,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Button,
} from '@achieve/ascend'
import { Typography, LinkButton } from 'components/Contentful'
import { TitleDivider } from 'components/TitleDivider'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { useMemoizedContentGetter } from 'utils/contentful'
import { useEffect, useRef, useState } from 'react'
import { _get } from 'utils/shared'
import { HandwrittenText } from 'components/HandwrittenText'
import { AchieveLink } from 'components/AchieveLink'
import { GET_STARTED_ROUTE_DR } from 'constants/navigation'
import { addUrlParameter } from 'utils/shared'
import { mainProductConfig } from 'constants/mainProductConfig'
import { PaperCardBadge } from './PaperCardBadge'
import { CardSkinnyCarousel, PaperCarouselCards } from './CardSkinnyCarousel'

function SkinnyFormSection({ content, onChangeSection }) {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.md)

  const {
    title,
    summary,
    eyebrow,
    exampleTitle,
    exampleLabelOne,
    exampleLabelTwo,
    exampleLabelThree,
    exampleLabelFour,
    exampleValueOne,
    exampleValueTwo,
    exampleValueThree,
    exampleValueFour,
    disclaimer,
    badgeExample,
    config,
    handwrittenRules,
    grid,
  } = useMemoizedContentGetter(content, [
    'title',
    'summary',
    'eyebrow',
    'exampleTitle',
    'exampleLabelOne',
    'exampleLabelTwo',
    'exampleLabelThree',
    'exampleLabelFour',
    'exampleValueOne',
    'exampleValueTwo',
    'exampleValueThree',
    'exampleValueFour',
    'disclaimer',
    'badgeExample',
    'config',
    'handwrittenRules',
    'grid',
  ])
  const buttonActionImg = _get(handwrittenRules, 'gridItemsCollection.items[0]', null)
  const slider = config?.jsonContent?.slider || {
    min: 0,
    max: 100000,
    step: 1000,
    maxPlus: true,
  }
  const sliderQueryParam = config?.jsonContent?.sliderQueryParam || 'loan_amount'
  const ctaLinkBase = config?.jsonContent?.ctaLinkBase || GET_STARTED_ROUTE_DR
  const variant = {
    title: isMobile ? 'headingMd' : 'displayLg',
    titleFontWeight: 'medium',
    subtitle: 'bodyLg',
    subtitleFontWeight: 'regular',
  }
  const sliderQuestion = config?.jsonContent?.sliderQuestion || 'How much debt do you have?'
  const sliderStatsTitle = config?.jsonContent?.sliderStatsTitle || 'Do you own your home?'
  const ctaText = config?.jsonContent?.ctaText || 'Get started'
  const ctaCustomizeText = config?.jsonContent?.ctaCustomizeText || 'Customize your plan'
  const ctaCustomizeLink = config?.jsonContent?.ctaCustomizeLink || GET_STARTED_ROUTE_DR
  const trackingNameCarouselControls = config?.jsonContent?.trackingNameCarouselControls
  const sectionVariation = config?.jsonContent?.sectionVariation || null
  const formVariation = config?.jsonContent?.formVariation || null
  const [sliderValue, setSliderValue] = useState(config?.jsonContent?.initialValue || 50000)
  const [ctaHref, setCtaHref] = useState('')
  const dollarUSLocale = Intl.NumberFormat('en-US')
  const [ownHome, setOwnHome] = useState(null)
  const [changeSlider, setChangeSlider] = useState(false)
  const trackRefButton = useRef()
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  useEffect(() => {
    let url = addUrlParameter(ctaLinkBase, sliderQueryParam, sliderValue)
    url = addUrlParameter(ctaLinkBase, 'is_home_owner', ownHome)
    setCtaHref(url)
  }, [sliderValue, ctaLinkBase, ownHome, sliderQueryParam])

  function handleChange(e) {
    setSliderValue(e.target.value)
    setChangeSlider(true)
  }
  function handleChangeHome(e) {
    setOwnHome(e?.target?.value)
  }

  function ButtonContainer({ disabled = false }) {
    return formVariation === 'recommendedCards' ? (
      <Grid
        size={{ xs: 12 }}
        className={'flex items-center flex-col md:block md:[align-items:normal] md:flex-row'}
      >
        <AchieveLink
          track={{
            list_name: 'PRODUCT FORM',
            click_id: ctaText,
            click_text: `${
              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
            } |  ${ctaText}`,
            form_ss_amount: sliderValue,
            form_ss_homeowner: ownHome,
            nav_link_section: 'Product Form',
            track_event: 'button_click',
            event_action: 'button_click',
            event_type: 'button_click',
            click_type: 'Button Click',
          }}
          disabled={disabled}
          noLink
        >
          <div ref={trackRefButton}></div>
        </AchieveLink>
        <Button
          color="primary"
          variant="contained"
          data-testid="skinny-form-cta"
          disabled={disabled}
          fullWidth={isMobile}
          onClick={() => {
            trackRefButton?.current?.click()
            onChangeSection({
              formAmount: sliderValue,
              formHomeowner: ownHome,
              formAmountQueryParameter: sliderQueryParam,
            })
          }}
        >
          <Typography content={ctaText} variant="bodyLg" fontWeight="bold" />
        </Button>
      </Grid>
    ) : (
      <Grid
        size={{ xs: 12 }}
        className={'flex items-center flex-col md:block md:[align-items:normal] md:flex-row'}
      >
        <LinkButton
          track={{
            list_name: 'PRODUCT FORM',
            click_id: ctaText,
            click_text: `${
              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
            } |  ${ctaText}`,
            hero_card_text: title,
            form_ss_amount: sliderValue,
            form_ss_homeowner: ownHome,
            nav_link_section: 'Product Form',
            track_event: 'button_click',
            event_action: 'button_click',
            event_type: 'button_click',
            click_type: 'Button Click',
          }}
          content={{
            linkHref: ctaHref,
            linkText: ctaText,
          }}
          typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
          color="primary"
          variant="contained"
          data-testid="skinny-form-cta"
          disabled={disabled}
          fullWidth={isMobile}
          underline="hover"
        />
        {!isMobile && buttonActionImg && (
          <div
            className={
              'w-[345px] mt-4 md:translate-x-8 md:w-[95%] lg:translate-x-16 xl:translate-x-16 xl:w-full'
            }
          >
            <HandwrittenText
              alt={buttonActionImg?.mediaAltText}
              content={
                isMobile ? buttonActionImg?.mobileMediaContent : buttonActionImg?.mediaContent
              }
            />
          </div>
        )}
      </Grid>
    )
  }

  function sectionVariant() {
    if (sectionVariation === 'carousel') {
      return (
        <PaperCarouselCards
          gridItems={grid?.gridItemsCollection?.items}
          trackingNameCarouselControls={trackingNameCarouselControls}
        />
      )
    } else {
      return (
        <PaperCardBadge
          badgeExample={badgeExample}
          exampleTitle={exampleTitle}
          stats={[
            { label: exampleLabelOne, value: exampleValueOne, testIdPrefix: 'one' },
            { label: exampleLabelTwo, value: exampleValueTwo, testIdPrefix: 'two' },
            { label: exampleLabelThree, value: exampleValueThree, testIdPrefix: 'three' },
            {
              label: exampleLabelFour,
              value: exampleValueFour,
              testIdPrefix: 'four',
              fontWeight: 'regular',
            },
          ]}
        />
      )
    }
  }

  return (
    <>
      <TitleDivider
        className={''}
        eyebrowClassName={'pb-2 md:pb-4'}
        eyebrow={eyebrow}
        eyebrowBlack
        subtitle={!isMobile && summary}
        title={title}
        isMobile={isMobile}
        data-testid="skinny-form-title"
        variants={variant}
        subtitleTag="p"
      />
      <Grid
        container
        data-testid="desktop-product-hero-container"
        className={'justify-center md:mt-16'}
      >
        <Grid container size={{ xs: 12, md: 5, xl: 6 }}>
          <Grid size={{ xs: 12, md: sectionVariation === 'carousel' ? 11 : 10 }}>
            <Paper
              className={
                'shadow-none relative z-2 bg-white border border-neutral-75 mb-16 p-4 rounded-md border-solid md:bg-transparent md:mb-0 md:p-0 md:rounded-none md:border-[none] md:border-0'
              }
              data-testid="skinny-form-container"
            >
              <Typography
                content={sliderQuestion}
                variant={isMobile ? 'bodySm' : 'bodyLg'}
                className={'text-left mb-4'}
                data-testid="skinny-form-title"
              />
              <div className={'w-full'}>
                <Typography
                  content={`$${dollarUSLocale.format(sliderValue)}${
                    sliderValue === slider.max && slider.maxPlus ? '+' : ''
                  }`}
                  variant="bodySm"
                  fontWeight="medium"
                  className={'text-[#30f] text-right'}
                  data-testid="product-hero-dollar-value"
                />
                <Slider
                  aria-label="dollar-amount-selection-slider"
                  classes={{
                    mark: 'hidden',
                  }}
                  data-testid="skinny-form-input"
                  value={sliderValue}
                  step={slider.step}
                  min={slider.min}
                  max={slider.max}
                  marks={[
                    {
                      value: slider.min,
                      label: (
                        <Typography
                          content={`$${dollarUSLocale.format(slider.min)}`}
                          variant="bodySm"
                          fontWeight="regular"
                          className={'translate-x-6 text-[#c0cbd8]'}
                          data-testid="skinny-form-min"
                        />
                      ),
                    },
                    {
                      value: slider.max,
                      label: (
                        <Typography
                          content={`$${dollarUSLocale.format(slider.max)}${
                            slider.maxPlus ? '+' : ''
                          }`}
                          variant="bodySm"
                          fontWeight="regular"
                          className={'-translate-x-8 text-[#c0cbd8]'}
                          data-testid="skinny-form-max"
                        />
                      ),
                    },
                  ]}
                  onChange={handleChange}
                />
              </div>
              <Divider margin={2} className={'mb-4 pb-4'} />
              <Typography
                content={sliderStatsTitle}
                variant={isMobile ? 'bodySm' : 'bodyLg'}
                className={'mb-4 data-[disabled="true"]:text-[#c0cbd8]'}
                data-testid="skinny-form-stats-title"
                data-disabled={!changeSlider}
              />
              {changeSlider && (
                <RadioGroup
                  row
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="radio-buttons-group"
                  value={ownHome}
                  onChange={handleChangeHome}
                  className={''}
                  data-testid="skinny-form-radio-group"
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              )}
              <Divider margin={2} className={'mb-4 pb-4'} />
              {ButtonContainer({ disabled: ownHome === null })}
            </Paper>
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: sectionVariation === 'carousel' ? 6 : 5,
            xl: 6,
          }}
          alignSelf="flex-start"
          position="relative"
        >
          {sectionVariant()}
          {disclaimer && (
            <Grid className={'mb-8 px-4 py-0'}>
              <Typography
                content={disclaimer?.textContent}
                variant="bodyXs"
                data-testid="example-disclaimer"
              />
            </Grid>
          )}
          {isMobile && (
            <LinkButton
              className={'mt-8'}
              track={{
                list_name: 'PRODUCT FORM CTA',
                click_position: '0',
                nav_link_section: 'Product Form',
                track_event: 'internal_campaign_click',
                event_action: 'button_click',
                event_type: 'button_click',
              }}
              content={{
                linkHref: ctaCustomizeLink,
                linkText: ctaCustomizeText,
              }}
              typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
              color="primary"
              variant="text"
              data-testid="skinny-form-cta"
              fullWidth={isMobile}
            />
          )}
        </Grid>
      </Grid>
    </>
  )
}

export { SkinnyFormSection, CardSkinnyCarousel }
export default SkinnyFormSection
